import clsx from 'clsx';
import capitalize from 'lodash.capitalize';
import { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import BlockIcon from '../../../../assets/icons/block.svg?react';
import EditIcon from '../../../../assets/icons/edit.svg?react';
import DrivingIcon from '../../../../assets/icons/tacho_driving.svg?react';
import RestingIcon from '../../../../assets/icons/tacho_rest.svg?react';
import WorkingIcon from '../../../../assets/icons/tacho_working.svg?react';
import {
  useInitialLogbookFormTimeline,
  useLogbookEditedTimeline,
  useLogbookFormState,
  useTimelineTotals,
} from '../../../../services/logbook';
import {
  DriverStatus,
  TimelinePeriod as TimelinePeriodProps,
} from '../../../../types/logbook';
import {
  LOBOOK_EDIT_INTERVAL_MINUTES,
  NUMBER_OF_DISPLAY_CELLS,
  NUMBER_OF_EDIT_COLS,
} from '../../constants';
import { convertToTimelinePeriods, minutesToTimeString } from '../../utils';
import styles from './Timeline.module.css';
import { TimelineStatusToRowNr, useMiltiSelectTimeline } from './utils';

interface EditButtonProps {
  onClick?: () => void;
}
export const EditButton: FC<EditButtonProps> = ({ onClick }) => {
  const { $t } = useIntl();
  return (
    <div
      className={styles.edit}
      onClick={onClick}
      aria-label={$t({ id: 'Edit' })}
    >
      <EditIcon className={styles.icon} />
    </div>
  );
};

export const RemovableCell = ({ status }: { status: DriverStatus }) => {
  return (
    <div className={clsx(styles.removable, styles[status.toLowerCase()])} />
  );
};

export const TimelinePeriod: FC<TimelinePeriodProps> = ({
  from,
  to,
  status,
}) => {
  const { $t } = useIntl();
  const [fromHours, fromMinutes] = from.split(':').map(Number);
  const [toHours, toMinutes] = to.split(':').map(Number);

  const translateFrom = Math.round(((fromHours * 60 + fromMinutes) / 60) * 50);
  const translateTo = Math.round(((toHours * 60 + toMinutes) / 60) * 50);
  const width = translateTo - translateFrom;

  const label = `${from.substring(0, 5)} - ${to.substring(0, 5)}`;

  return (
    <div
      className={clsx(styles.booked, styles[status.toLowerCase()])}
      style={{
        gridRow: TimelineStatusToRowNr[status],
        gridColumnStart: 2,
        gridColumnEnd: 2,
        marginLeft: `${translateFrom}px`,
        width: `${width}px`,
      }}
      aria-label={`${$t({
        id: capitalize(status.toLowerCase()),
      })} ${label}`}
    >
      {width > 49 && <div className={styles.label}>{label}</div>}
    </div>
  );
};

export const Timelineheader: FC = () => {
  const totals = useTimelineTotals();

  return (
    <div className={styles.header}>
      <div className={clsx(styles.head, styles.cell, styles.corner)} />
      <div className={clsx(styles.head, styles.cell)}>
        <DrivingIcon className={clsx(styles.icon, styles.driving)} />
        <div>{totals.DRIVING.substring(0, 5)}</div>
      </div>
      <div className={clsx(styles.head, styles.cell)}>
        <RestingIcon className={clsx(styles.icon, styles.resting)} />
        <div>{totals.RESTING.substring(0, 5)}</div>
      </div>
      <div className={clsx(styles.head, styles.cell)}>
        <WorkingIcon className={clsx(styles.icon, styles.working)} />
        <div>{totals.WORKING.substring(0, 5)}</div>
      </div>
    </div>
  );
};

export const DisplayTimeline: FC = () => {
  const time = useLogbookFormState()?.time;

  return (
    <>
      <div className={styles.timeline}>
        <div />
        {Array(NUMBER_OF_DISPLAY_CELLS)
          .fill(null)
          .map((_v, i) => (
            <div key={i} className={styles.cell}>{`${i}:00`}</div>
          ))}
        <div />
        {Array(NUMBER_OF_DISPLAY_CELLS)
          .fill(null)
          .map((_v, i) => (
            <div key={i} className={styles.cell}></div>
          ))}
        <div />
        {Array(NUMBER_OF_DISPLAY_CELLS)
          .fill(null)
          .map((_v, i) => (
            <div key={i} className={styles.cell}></div>
          ))}
        <div />
        {Array(NUMBER_OF_DISPLAY_CELLS)
          .fill(null)
          .map((_v, i) => (
            <div key={i} className={styles.cell}></div>
          ))}
      </div>
      <div className={clsx(styles.timeline, styles.bookedTimeline)}>
        <Timelineheader />
        {convertToTimelinePeriods(time).map((props) => (
          <TimelinePeriod key={props.from} {...props} />
        ))}
      </div>
    </>
  );
};

const shortTimeFromIndex = (index: number) =>
  minutesToTimeString(index * LOBOOK_EDIT_INTERVAL_MINUTES).substring(0, 5);

export const EditTimeline: FC = () => {
  const { $t } = useIntl();
  const initial = useInitialLogbookFormTimeline();
  const timeline = useLogbookEditedTimeline();

  const { handleTouchEnd, handleTouchMove, handleTouchStart, scrollRef } =
    useMiltiSelectTimeline();

  const label = useCallback(
    (i: number, on: boolean, status: DriverStatus) => {
      return `${$t({
        id: capitalize(status.toLowerCase()),
      })} ${shortTimeFromIndex(i)}. ${$t({ id: on ? 'On' : 'Off' })}`;
    },
    [$t]
  );

  return (
    <>
      <div
        className={styles.editTimeline}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchStart={handleTouchStart}
        onMouseUp={handleTouchEnd}
        onMouseDown={handleTouchStart}
        onMouseMove={handleTouchMove}
        ref={scrollRef}
      >
        <div />
        {Array(NUMBER_OF_EDIT_COLS)
          .fill(null)
          .map((_v, i) => (
            <div key={i} className={styles.cell}>
              {shortTimeFromIndex(i)}
            </div>
          ))}
        <div />
        {Array(NUMBER_OF_EDIT_COLS)
          .fill(null)
          .map((_v, i) =>
            timeline[i] === 'DRIVING' ? (
              <div
                key={i}
                className={styles.cell}
                aria-label={label(i, true, 'DRIVING')}
              >
                <RemovableCell status="DRIVING" />
              </div>
            ) : (
              <div
                key={i}
                className={clsx(styles.cell, styles.unused)}
                aria-label={label(i, false, 'DRIVING')}
                data-index={i}
                data-type="DRIVING"
                data-editable
              ></div>
            )
          )}
        <div />
        {Array(NUMBER_OF_EDIT_COLS)
          .fill(null)
          .map((_v, i) => {
            const editable = initial[i] !== 'DRIVING';

            return timeline[i] === 'RESTING' ? (
              <div
                key={i}
                className={styles.cell}
                aria-label={label(i, true, 'RESTING')}
              >
                <RemovableCell status="RESTING" />
              </div>
            ) : (
              <div
                key={i}
                className={clsx(styles.cell, styles.unused)}
                aria-label={label(i, false, 'RESTING')}
                data-index={i}
                data-type="RESTING"
                data-editable={editable}
              >
                {!editable && <BlockIcon aria-label="blocked" />}
              </div>
            );
          })}
        <div />
        {Array(NUMBER_OF_EDIT_COLS)
          .fill(null)
          .map((_v, i) => {
            const editable = initial[i] !== 'DRIVING';

            return timeline[i] === 'WORKING' ? (
              <div
                key={i}
                className={styles.cell}
                aria-label={label(i, true, 'WORKING')}
              >
                <RemovableCell status="WORKING" />
              </div>
            ) : (
              <div
                key={i}
                className={clsx(styles.cell, styles.unused)}
                aria-label={label(i, false, 'WORKING')}
                data-index={i}
                data-type="WORKING"
                data-editable={editable}
              >
                {!editable && <BlockIcon aria-label="blocked" />}
              </div>
            );
          })}
      </div>
      <div
        className={clsx(
          styles.editTimeline,
          styles.bookedTimeline,
          styles.noEvents
        )}
      >
        <Timelineheader />
      </div>
    </>
  );
};
